'use client';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { VscClose } from 'react-icons/vsc';
import { Root, Content, Portal, Overlay } from '@radix-ui/react-dialog';

import GlobalLoader from '../GlobalLoader';
import VillageEnquiryForm from '../EnquiryForm/VillageEnquiryForm';

import { getTranslatedSchema } from '../EnquiryForm/villageSchema';
import { IEnquiryFormDictionary } from '@/dictionaries/components/enquiryForm/types';
import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import { IModalsDictionary } from '@/dictionaries/components/modals/types';
import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import useDatePickerContext from '@/hooks/useDatePickerContext';
import { cn } from '@/lib/utils';
import { Village } from '@/services/types';
import { getSelectDescription } from '../ContactForm/helpers';
import { useLocale } from '@/hooks/useLocale';
import useUserContext from '@/hooks/useUserContext';

import '../DatePicker/styles.css';

export type Dicts = {
  datePickerDict: IDatePickerDictonary;
  formDict: IEnquiryFormDictionary;
  countriesDict: ICountriesDictionary;
  modalsDict: IModalsDictionary;
};

type Props = {
  village: Village;
  lang: string;
  dicts: Dicts;
  handleModalState?: (open: boolean) => void;
  onlyContent?: boolean;
};

type ContentProps = {
  willUnmount: boolean;
  closeModal: () => void;
} & Props;

export default function NotifyVillageAvailabilityModal(props: Props) {
  const [willUnmount, setWillUnmount] = useState(false);

  const { enquiryModalState, setEnquiryModalState } = useDatePickerContext();

  const enquiryModalIsOpen = enquiryModalState === 'price-preview';

  const handleCloseModal = () => {
    setWillUnmount(true);
    setTimeout(() => {
      setEnquiryModalState(null);
      setWillUnmount(false);
    }, 200);
  };

  const contentProps = {
    willUnmount,
    closeModal: handleCloseModal,
  };

  if (props.onlyContent) {
    return (
      <NotifyVillageAvailabilityModalContent {...props} {...contentProps} />
    );
  }

  return (
    <Root
      open={enquiryModalIsOpen}
      onOpenChange={(open) => {
        if (!open) handleCloseModal();
      }}>
      <Portal>
        <Overlay className="DatePickerOverlay">
          <div className={cn('DatePickerContent 2xl:!my-0 !bg-transparent')}>
            <Content
              onInteractOutside={(e) => {
                e.preventDefault();
                handleCloseModal();
              }}>
              <NotifyVillageAvailabilityModalContent
                {...props}
                {...contentProps}
              />
            </Content>
          </div>
        </Overlay>
      </Portal>
    </Root>
  );
}

function NotifyVillageAvailabilityModalContent(props: ContentProps) {
  const { enquiryModalState, setEnquiryModalState, setSuccessModalIsOpen } =
    useDatePickerContext();

  const { customer, user, isLogged } = useUserContext();

  const enquiryModalIsOpen = enquiryModalState !== null;

  const { formDict } = props.dicts;

  const lang = useLocale();

  const formDefaultValues = useMemo(() => {
    const { name, code, id, countries } = getSelectDescription(
      lang,
      props.dicts.countriesDict,
    );

    if (isLogged) {
      const country = countries.find(
        (country) => country.id == customer?.country?.id,
      );
      const countryPhone = countries.find(
        (country) => country.id == customer?.phoneCode?.id,
      );

      return {
        name: user.firstName,
        surname: user.lastName,
        email: user.emailCanonical,
        phone: customer.phone ? customer.phone : '',
        country_info: {
          name: country ? country?.name : name,
          code: country ? country?.code : code,
          id: country ? country?.id : id,
          phone: {
            code: countryPhone ? countryPhone?.id : id,
            country: countryPhone ? countryPhone?.name : name,
          },
        },
      };
    } else {
      return {
        name: '',
        surname: '',
        email: '',
        phone: '',
        country_info: {
          name,
          code,
          id,
          phone: {
            code: id,
            country: name,
          },
        },
      };
    }
  }, [props.dicts.countriesDict, customer, user, isLogged, lang]);

  const schema = getTranslatedSchema(formDict.errors);

  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...formDefaultValues,
      adults: 0,
      children: 0,
      infants: 0,
      dates: {
        start: null,
        end: null,
      },
    },
  });

  return (
    <>
      {enquiryModalIsOpen && (
        <div
          className={cn(
            'relative flex justify-start sm:justify-center items-start sm:items-center desktop:w-full h-fit md:h-full md:mt-[15vh] desktop:mt-0 z-[2147483649]',
            {
              'animate-mobile-close-modal ': props.willUnmount,
            },
            {
              'animate-mobile-close-modal md:animate-close-modal':
                props.willUnmount && props.onlyContent,
            },
            {
              'animate-mobile-open-modal md:animate-open-modal':
                !props.willUnmount && props.onlyContent,
            },
            {
              'fixed top-0 left-0 md:mt-0 md:top-1/2 md:left-1/2  md:-translate-x-1/2 md:-translate-y-1/2 md:w-[calc(100%+5rem)]':
                props.onlyContent,
            },
          )}>
          <div className="relative flex flex-col justify-start items-center w-full h-fit md:min-h-full md:max-h-fit px-[60px] pt-[50px] desktop:py-[50px] bg-white gap-4">
            <GlobalLoader
              className="absolute z-[2147483650]"
              isLoading={form.formState.isSubmitting}
            />
            <button
              className="absolute top-[10px] desktop:-top-[10px] right-[10px] desktop:-right-[10px] flex justify-center items-center w-10 h-10 bg-white shadow-default rounded-full z-10"
              onClick={props.closeModal}>
              <VscClose className="w-7 h-7 text-gray-500" />
            </button>
            <div className="flex flex-col">
              <h2 className="mb-2 text-4xl font-semibold">
                {formDict.datePickerEnquiry.title}
              </h2>
              <p className="mb-4">{formDict.datePickerEnquiry.description}</p>
              <VillageEnquiryForm
                atModal
                form={form}
                setSuccessModalIsOpen={setSuccessModalIsOpen}
                onSuccess={() => {
                  setEnquiryModalState(null);
                  if (props.handleModalState) {
                    props.handleModalState(false);
                  }
                }}
                {...props}
                {...props.dicts}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
